import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import styled from "styled-components";

import { useMap } from "common/hooks/useMap";
import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";

import { BaseForm } from "common/components/templates/BaseForm";
import { DashboardTemplate } from "common/components/templates/DashboardTemplate";
import { Map2D } from "common/components/map/Map2D";
import { CURSOR } from "common/components/map/common/osm/components/CursorDrawer";
import { PolygonDrawer } from "common/components/map/common/osm/components/PolygonDrawer";
import { PolygonViewSettings } from "common/components/map/common/osm/components/PolygonViewSettings";
import { SFilterCheck, SFilterSection } from "./components/MapFilterControls";
import { MapTopPanel } from "./components/MapTopPanel";

import { createDocumentTitle } from "utils/commonTools";
import {
  ChartOverlayData,
  ChartOverlayDrawer,
} from "common/components/map/common/osm/components/ChartOverlayDrawer";
import { ChartViewSettings } from "common/components/map/common/osm/components/ChartViewSettings";
import { Coordinates, PolygonArea } from "common/components/map/common/types/MapData";
import { NameID } from "common/types/NameID";

import { useWorkReport } from "pages/workreport/hooks/useWorkReport";
import { TaskReportPanel } from "pages/workreport/components/TaskReportPanel";
import { Task, WorkReportData, WorkReports } from "pages/workreport/types/WorkReportData";
import { useMapWork } from "./hooks/useMapWork";
import { Period, WorkReportMeta } from "pages/workreport/types/WorkReportMeta";
import SearchContext from "common/store/SearchContext";
import { WorkReportType } from "pages/workreport/types/WorkReportType";
import { ReportData } from "./types/ReportData";

export const MapWorkPage = ({ pageTitle }: { pageTitle: string }) => {
  const isInitializedRef = useRef(false);
  const { selectedPark } = useContext(AuthContext);
  const { searchFrom, searchTo } = useContext(SearchContext);
  const { setIsLoading, setLoadingMessage, setErrorMessage } = useContext(StatusContext);

  const { loadMetaData } = useWorkReport();
  const { buildAreaPolygons } = useMap();
  const { searchData, convertToTasksOverlays } = useMapWork();

  const [metaData, setMetaData] = useState<WorkReportMeta>();
  const [polygons, setPolygons] = useState<PolygonArea[]>([]);
  const [chartData, setChartData] = useState<ChartOverlayData[]>([]);
  const [reportData, setReportData] = useState<ReportData<WorkReports>[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<NameID[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<number>();
  const [selectedPin, setSelectedPin] = useState<Coordinates>();
  const [selectedDetails, setSelectedDetails] = useState<WorkReportData[]>([]);
  // テーブルに日付を追加するためのもの
  const [detailsDates, setDetailsDates] = useState<Date[]>();
  const [detailsPeriods, setDetailsPeriods] = useState<NameID[]>();
  const [showCharts, setShowCharts] = useState(true);
  const [showPins, setShowPins] = useState(true);
  const [showPolygons, setShowPolygons] = useState(true);
  const [showPolygonLabels, setShowPolygonLabels] = useState(false);

  const normalizePeriod = (period: Period | NameID): NameID => ({
    id: period.id ?? -1,
    name: period.name,
  });

  const initialize = async () => {
    if (!selectedPark) return;

    isInitializedRef.current = true;

    const meta = await loadMetaData(selectedPark.parkId, WorkReportType.ParkWork);
    if (!meta.succeeded) {
      setErrorMessage(`メタデータの取得に失敗しました。${meta.msg}`);
      return;
    }

    setMetaData(meta.data);
    setPolygons(meta.data ? buildAreaPolygons(meta.data.areas) : []);
    setSelectedCategories(meta.data?.periods.map(normalizePeriod) ?? []);
    await handleSearch(searchFrom, searchTo, meta.data?.areas);
  };

  useEffect(() => {
    document.title = createDocumentTitle(pageTitle);
    if (isInitializedRef.current) return;

    const f = async () => initialize();
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  /**
   * 検索処理を実行する
   */
  const handleSearch = async (from: Date, to: Date, areasOverride?: WorkReportMeta["areas"]) => {
    if (!selectedPark) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);
    setLoadingMessage("データの検索中");

    const res = await searchData(selectedPark.parkId, from, to);

    setIsLoading(false);
    if (!res.succeeded) {
      setErrorMessage(res.msg || "");
      return;
    }
    const resData = res.data ?? [];
    setReportData(resData);
    setSelectedAreaId(undefined);
    setSelectedPin(undefined);
    setSelectedDetails([]);
    setDetailsDates(undefined);
    setDetailsPeriods(undefined);

    const areas = areasOverride ?? metaData?.areas;
    if (!areas || areas.length === 0) setChartData([]);
    else setChartData(convertToTasksOverlays(resData, areas));
  };

  /**
   *グラフのクリック
   */
  const handleBarSelected = (areaId: number, pin?: Coordinates) => {
    setSelectedAreaId(areaId);
    setSelectedPin(pin);
  };

  const handleBarDeselected = () => {
    setSelectedAreaId(undefined);
    setSelectedPin(undefined);
    setSelectedDetails([]);
    setDetailsDates(undefined);
    setDetailsPeriods(undefined);
  };

  /**
   * フィルターの変更
   */
  const toggleCategory = (category: NameID, checked: boolean) => {
    setSelectedCategories((prev) => {
      if (checked) {
        if (prev.some((x) => x.id === category.id)) return prev;
        return [...prev, category];
      }
      return prev.filter((x) => x.id !== category.id);
    });
  };

  /**
   * 全てチェックの場合
   */
  const toggleAllCategories = (checked: boolean) => {
    if (checked) {
      setSelectedCategories(periods);
    } else {
      setSelectedCategories([]);
    }
  };

  const matchArea = (task: Task, areaId: number) => {
    if (areaId >= 0) {
      if (task.areas?.some((x) => x.id === areaId)) return true;
      return task.point?.area?.id === areaId;
    }

    if (areaId === -1) {
      return !task.areas?.length && !task.point;
    }

    if (areaId === -2) {
      return !!task.point && !task.point.area;
    }

    return false;
  };

  const matchPin = (task: Task, pin: Coordinates) => {
    return task.point?.coords.x === pin.x && task.point?.coords.y === pin.y;
  };

  useEffect(() => {
    const selectedCategoryIds = new Set(selectedCategories.map((x) => x.id));
    const details: WorkReportData[] = [];
    const dates: Date[] = [];
    const periods: NameID[] = [];

    reportData.forEach((row) => {
      row.value.reports.forEach((report) => {
        const normalizedPeriod = normalizePeriod(report.period);
        const filteredTasks = report.tasks.filter((task) => {
          if (!selectedCategoryIds.has(normalizedPeriod.id)) return false;
          if (selectedAreaId !== undefined && !matchArea(task, selectedAreaId)) return false;
          if (selectedPin && !matchPin(task, selectedPin)) return false;
          return true;
        });

        if (filteredTasks.length === 0) return;

        details.push({
          ...report,
          tasks: filteredTasks,
        });
        dates.push(...filteredTasks.map(() => row.date));
        periods.push(...filteredTasks.map(() => normalizedPeriod));
      });
    });

    setSelectedDetails(details);
    setDetailsDates(dates.length > 0 ? dates : undefined);
    setDetailsPeriods(periods.length > 0 ? periods : undefined);
  }, [reportData, selectedAreaId, selectedPin, selectedCategories]);

  // 対象期間の選択
  const periods = useMemo(() => {
    return metaData?.periods.map(normalizePeriod) ?? [];
  }, [metaData?.periods]);

  const filteredChartData = useMemo(
    () =>
      chartData
        .map((x) => ({
          ...x,
          bars: x.bars.filter((b) => selectedCategories.find((y) => y.id === b.label.id)),
        }))
        .filter((x) => x.bars.length > 0),
    [chartData, selectedCategories],
  );

  const selectedArea = useMemo(
    () => metaData?.areas.find((x) => x.area.id === selectedAreaId)?.area,
    [metaData?.areas, selectedAreaId],
  );

  const allChecked =
    periods.length > 0 && periods.every((x) => selectedCategories.find((y) => y.id === x.id));

  return (
    <DashboardTemplate>
      <MapTopPanel pageTitle={pageTitle} onSearch={handleSearch} />

      <BaseForm className="p-3">
        <SMapForm>
          <SMapPane>
            <Map2D
              cursorType={CURSOR.NONE}
              mapFunctions={[
                <PolygonDrawer
                  key="polygons"
                  polygons={polygons}
                  showPolygons={showPolygons}
                  showLabels={showPolygonLabels}
                />,
                <ChartOverlayDrawer
                  key="charts"
                  chartData={filteredChartData}
                  showCharts={showCharts}
                  showPins={showPins}
                  onBarSelected={handleBarSelected}
                  onBarDeselected={handleBarDeselected}
                />,
                ...(polygons.length > 0
                  ? [
                      <PolygonViewSettings
                        key="polygon-view-settings"
                        showPolygons={showPolygons}
                        onShowPolygonsChange={setShowPolygons}
                        showLabels={showPolygonLabels}
                        onShowLabelsChange={setShowPolygonLabels}
                      />,
                    ]
                  : []),
                ...(filteredChartData.length > 0
                  ? [
                      <ChartViewSettings
                        key="chart-view-settings"
                        showCharts={showCharts}
                        onShowChartsChange={setShowCharts}
                        showPins={showPins}
                        onShowPinsChange={setShowPins}
                      />,
                    ]
                  : []),
              ]}
            />
          </SMapPane>
          <SFilterSection>
            <SFilterCheck
              type="checkbox"
              label="全て表示"
              $checked={allChecked}
              $isPrimary={true}
              checked={allChecked}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                toggleAllCategories(e.currentTarget.checked)
              }
            />
            {periods.map((x) => (
              <SFilterCheck
                key={x.id}
                type="checkbox"
                label={x.name}
                $checked={selectedCategories.some((y) => y.id === x.id)}
                checked={selectedCategories.some((y) => y.id === x.id)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  toggleCategory(x, e.currentTarget.checked)
                }
              />
            ))}
          </SFilterSection>
        </SMapForm>

        <SDetailSection>
          {selectedArea && <SDetailHeader>{`内訳: ${selectedArea.name}`}</SDetailHeader>}
          {metaData && selectedDetails.length > 0 && (
            <SDetailTableWrapper>
              <TaskReportPanel
                tabName={""}
                meta={metaData}
                tasks={selectedDetails.map((x) => x.tasks).flat()}
                dates={detailsDates}
                periods={detailsPeriods}
                readonly={true}
                isMapAvailable={true}
                tasksChanged={(_tasks) => {}}
                showNumber={true}
              />
            </SDetailTableWrapper>
          )}
        </SDetailSection>
      </BaseForm>
    </DashboardTemplate>
  );
};

const SMapForm = styled(Row)`
  flex: 1;
  height: 40vh;
  display: flex;
  flex-direction: column;
`;

const SMapPane = styled.div`
  flex: 1 1 0;
  min-height: 0;
`;

const SDetailSection = styled.div`
  margin-top: 0.75rem;
`;

const SDetailTableWrapper = styled.div`
  max-height: 38vh;
  overflow: auto;

  .table-responsive {
    overflow: visible;
  }

  table {
    margin-bottom: 0;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: hsl(200, 60%, 85%);
  }
`;

const SDetailHeader = styled.div`
  font-weight: 600;
`;
